import PropTypes from 'prop-types';
import React from 'react';
import Helmet from 'react-helmet';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';
import Link from 'brastrap/containers/link/LinkContainer';
import addAuthenticationToPopulateStore from './AuthenticatedContainer/authenticated-populate-store';

const DEFAULT_AUTHENTICATION_CRITERIA = 'isAuthenticated';

/**
 * @param {Component} Component
 * @param {Object} props
 * @param {Object} options
 * @return {*}
 */
export function requireAuthentication(Component, props, options) {
  class AuthenticatedContainer extends React.Component {
    /**
     * Redirect the user to the login link url.
     */
    componentDidMount() {
      if (
        !this.getIsAuthenticatedOrIdentified() &&
        this.props.redirect &&
        this.props.loginLink.url
      ) {
        const pathname = this.props.basePath + this.props.loginLink.url;
        this.props.history.push({
          pathname,
          query: {},
          state: { nextPathname: this.props.location.pathname },
        });
      }
    }

    /**
     * @return {*}
     */
    getIsAuthenticatedOrIdentified() {
      return this.props[this.props.authenticationCriteria];
    }

    /**
     * @return {XML}
     */
    render() {
      const isAuthenticatedOrIdentified = this.getIsAuthenticatedOrIdentified();
      const { loginLink, noindex, ...componentProps } = this.props;
      const displayLoginLink =
        !isAuthenticatedOrIdentified && loginLink.display;

      return (
        <div>
          {noindex && (
            <Helmet meta={[{ name: 'robots', content: 'noindex' }]} />
          )}
          {isAuthenticatedOrIdentified && <Component {...componentProps} />}
          {displayLoginLink && (
            <p>
              <Link href={loginLink.url} title={loginLink.title}>
                {loginLink.text}
              </Link>
            </p>
          )}
        </div>
      );
    }
  }

  AuthenticatedContainer.propTypes = {
    authenticationCriteria: PropTypes.string,
    basePath: PropTypes.string,
    componentProps: PropTypes.object,
    history: PropTypes.shape({
      push: PropTypes.func,
    }),
    isAuthenticated: PropTypes.bool,
    isIdentified: PropTypes.bool,
    loginLink: PropTypes.shape({
      display: PropTypes.bool,
      title: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
    }),
    redirect: PropTypes.bool,
    router: PropTypes.object,
    location: PropTypes.object,
    noindex: PropTypes.bool,
  };

  AuthenticatedContainer.defaultProps = _.merge(
    {
      // Can be either isAuthenticated or isIdentified
      authenticationCriteria: DEFAULT_AUTHENTICATION_CRITERIA,

      // Defines the text used in the link if the user is not authorised
      loginLink: {
        display: false,
        text: 'Please Login',
        title: 'Please login',
        url: 'login/',
      },

      // If not authorised to view component redirect to the url specified in loginLink.url
      redirect: true,
      noindex: false,
    },
    options
  );

  const mapStateToProps = state => ({
    basePath: state.app.config.basePath,
    componentProps: props,
    isAuthenticated: state.auth.isAuthenticated,
    isIdentified: state.auth.isIdentified,
  });

  const wrappedComponent = withRouter(
    connect(mapStateToProps)(AuthenticatedContainer)
  );

  let nextComponent = Component;

  /* Look for a populateStore method in our hierarchy so that server-side data population
   * can be carried out. This information seems not be available server/app.js:populateStore(),
   * hence we work it out here when we dynamically wrap a component. */
  do {
    wrappedComponent.populateStore = nextComponent.populateStore;
    nextComponent = nextComponent ? nextComponent.WrappedComponent : null;
  } while (wrappedComponent.populateStore === undefined && nextComponent);

  if (wrappedComponent.populateStore) {
    wrappedComponent.populateStore = addAuthenticationToPopulateStore(
      wrappedComponent.populateStore,
      options.authenticationCriteria || DEFAULT_AUTHENTICATION_CRITERIA
    );
  }

  return wrappedComponent;
}
